/**
 * PostHog analytics composable
 *
 * Provides a consistent interface for PostHog analytics throughout the application
 */
import { ref } from 'vue';
import type posthog from 'posthog-js';

// Create a reactive reference to hold the PostHog instance
const posthogInstance = ref<typeof posthog | null>(null);

/**
 * PostHog composable for analytics tracking
 */
export const usePostHog = () => {
  /**
   * Initialize PostHog with configuration
   */
  const init = (instance: typeof posthog) => {
    posthogInstance.value = instance;
  };

  /**
   * Track an event with PostHog
   */
  const capture = (
    eventName: string,
    properties?: Record<string, any>,
  ): boolean => {
    if (!posthogInstance.value) return false;

    try {
      posthogInstance.value.capture(eventName, properties);
      return true;
    } catch {
      // Silent fail in production
      return false;
    }
  };

  /**
   * Reset the current user to an anonymous state
   */
  const reset = (): boolean => {
    if (!posthogInstance.value) return false;

    try {
      posthogInstance.value.reset();
      return true;
    } catch {
      // Silent fail in production
      return false;
    }
  };

  return {
    posthog: posthogInstance.value,
    init,
    capture,
    reset,
  };
};
